import { useState, useMemo, useRef, useEffect } from 'react';
import Tabs from '@/components/tabs';
import { useTranslation } from 'react-i18next';
import Table from '@/components/tableDrawer/TableItems/table';
import ScreenHeader from '@/components/ScreenHeader';
import IconBtn from '@/components/ActionBtn/IconBtn/IconBtn';
import { Block } from '@/components/crRender';
import { getFileIcon } from '@/pages/requests/medical/requestDetails/components/PatientDocuments';
import PDFPopup from '@/components/popups/PDFPopup/PDFPopup';
import ImgPopup from '@/components/popups/imgPopup';
import { Link } from 'react-router-dom';

const Documents = ({ documents }) => {
  const { t } = useTranslation();
  const pdfRef = useRef(null);
  const imgRef = useRef(null);
  
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [triggerOpen, setTriggerOpen] = useState(false);

  const getFullLink = (docName) => {
    return docName ? `${window.WEB_LINK}${docName}` : '';
  }

  const tabs = useMemo(() => (
    documents?.map((doc) => ({
      label: t(doc?.name) || doc?.name,
      value: doc?.name,
      key: doc?.name
    })) || []
  ), [documents, t]);

  useEffect(() => {
    if (!activeTab && tabs?.length > 0) {
      setActiveTab(tabs[0].value);
    }
  }, [tabs, activeTab]);

  // @Ahmed Tamer
  //  any ideas to make it better ?
  useEffect(() => {
    if (triggerOpen && selectedDoc) {
      const isPdf = selectedDoc.name?.toLowerCase().endsWith('.pdf');
      if (isPdf && pdfRef.current) {
        pdfRef.current.click();
      } else if (!isPdf && imgRef.current) {
        imgRef.current.click();
      }
      setTriggerOpen(false);
    }
  }, [selectedDoc, triggerOpen]);

  const activeDocuments = useMemo(() => {
    return documents?.find(doc => doc.name === activeTab)?.data || [];
  }, [documents, activeTab]);

  const handleViewDocument = (doc) => {
    setSelectedDoc(doc);
    setTriggerOpen(true);
  };

  const handleDownloadDocument = (doc) => {
    const link = document.createElement('a');
    link.href = getFullLink(doc?.name);
    link.download = doc?.name?.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = useMemo(() => [
    {
      Header: t("file-name"),
      accessor: "name",
      Cell: ({ row: { original: doc } }) => {
        const fileName = doc?.name?.split('/').pop();
        return (
          <div className="w-100 d-flex align-items-center gap-2 doc-name">
            {getFileIcon(doc?.name)}
            <span>{fileName}</span>
          </div>
        );
      }
    },
    {
      Header: t("date"),
      accessor: "date",
    },
    {
      Header: t("actions"),
      id: "actions-column",
      Cell: ({ row: { original: doc } }) => (
        <div className="doc-actions d-flex gap-3">
          <IconBtn onClick={() => handleViewDocument(doc)} icon="solar:eye-outline" className="doc-action-btn" />
          <Link to={getFullLink(doc?.name)} target="_blank" download>
            <IconBtn icon="solar:download-outline" className="doc-action-btn" />
          </Link>
        </div>
      )
    }
  ], [t]);

  return (
    <div className="docs-section">
      <PDFPopup link={getFullLink(selectedDoc?.name)} name="doc-pdf-preview" title={selectedDoc?.name?.split('/').pop()} modalID={pdfRef} />
      <ImgPopup link={selectedDoc?.name} sourcePath={window.WEB_LINK} isHideText={true} refImg={imgRef} />
      <ScreenHeader title="clinical-documents-repository" icon="solar:folder-with-files-outline"
      className="bg-transparent mx-0 px-0 doc-header" />
      <Block isRender={documents?.length}>
        <Tabs className="mb-3" activeTab={activeTab} onApply={setActiveTab} List={tabs} />
        <div className="docs-table-wrap border-0">
          <Table columns={columns} data={activeDocuments} isPagination={false}
            isNeedFetch={false}
            TotalRows={activeDocuments.length}
            stickyActions={true}
          />
        </div>
      </Block>
      <Block isRender={!documents?.length}>
        <div className="alert alert-info mt-3">{t("no-data")}</div>
      </Block>
    </div>
  );
};

export default Documents;